import { Role } from '@/core/models/auth.interface';
import { buildAdminClientClosingMenuItems } from '@/pages/shared-deals/data-access/shared-deals.config';
import { ExtendedMenuItem } from '../menu.types';
import { AI_FEATURE_ENABLED } from '@/shared/utilities/feature-flags';
/**
 * Admin Menu Configuration
 *
 * Returns the menu items for Admin role users.
 * Separated from main menu component for better maintainability.
 *
 * @param logoutCallback - Function to call when user clicks logout
 * @param askBettaAlphaCallback - Function to call when user clicks "Ask BettaAlpha"
 * @returns Array of menu items for admin role
 */
export function getAdminMenuItems(logoutCallback: () => void, role: Role, askBettaAlphaCallback: () => void): ExtendedMenuItem[] {
    const isAdminUsersRole = role === Role.ADMIN_CLIENT_USERS;
    const isAdminDealPreparerRole =
        role === Role.ADMIN_DEAL_USER_PREPARER || role === Role.ADMIN_DEAL_USER_APPROVER;
    const isSuperAdmin = role === Role.SUPER_ADMIN;
    return [
        {
            label: 'Dashboard',
            iconPath: '/icons/dashboard.svg',
            routerLink: ['/admin/dashboard']
        } as ExtendedMenuItem,

        ...(!isAdminUsersRole ? [{
            label: 'Completed Investments',
            iconPath: '/icons/deals.svg',
            routerLink: ['/admin/investments']
        } as ExtendedMenuItem] : []),

        ...(AI_FEATURE_ENABLED ? [{
            label: 'Ask BettaAlpha',
            iconPath: '/icons/ai-sparkle.svg',
            command: askBettaAlphaCallback
        } as ExtendedMenuItem] : []),

        ...(!isAdminUsersRole ? [{
            label: 'Opportunities',
            menuType: 'submenu',
            iconPath: '/icons/deals.svg',
            items: [
                {
                    label: 'All Opportunities',
                    routerLink: ['/admin/deal-management/deals']
                },
                {
                    label: 'Draft',
                    routerLink: ['/admin/deal-management/deals/draft']
                },
                {
                    label: 'Diligence & Commercials',
                    routerLink: ['/admin/deal-management/deals/diligence-commercials']
                },
                {
                    label: 'Investment Committee',
                    routerLink: ['/admin/deal-management/deals/investment-committee']
                },
                {
                    label: 'Live',
                    routerLink: ['/admin/deal-management/deals/live']
                },
                {
                    label: 'Closing',
                    routerLink: ['/admin/deal-management/deals/closing'],
                },
                {
                    label: 'Completed',
                    routerLink: ['/admin/deal-management/deals/completed']
                },
                {
                    label: 'Declined',
                    routerLink: ['/admin/deal-management/deals/declined']
                },
                {
                    label: 'Missed',
                    routerLink: ['/admin/deal-management/deals/missed']
                },
                {
                    label: 'Watchlist',
                    routerLink: ['/admin/deal-management/deals/watchlist']
                }
            ]
        } as ExtendedMenuItem] : []),

        {
            label: 'Shared Opportunities',
            iconPath: '/icons/shared.svg',
            menuType: 'box-submenu',
            expanded: false,
            boxSections: [
                {
                    title: 'Shared with Wealth Manager',
                    icon: '/icons/wealth-manager.svg',
                    items: [
                        {
                            label: 'All Opportunities',
                            routerLink: ['/admin/deal-management/shared-opportunities/wm']
                        },
                        {
                            label: 'Open Opportunities',
                            routerLink: ['/admin/deal-management/shared-opportunities/wm/open']
                        },
                        {
                            label: 'Review Opportunities',
                            routerLink: ['/admin/deal-management/shared-opportunities/wm/review']
                        },
                        {
                            label: 'Live Opportunities',
                            routerLink: ['/admin/deal-management/shared-opportunities/wm/live']
                        },
                        {
                            label: 'Closing Opportunities',
                            routerLink: ['/admin/deal-management/shared-opportunities/wm/closing']
                        },
                        {
                            label: 'Completed Opportunities',
                            routerLink: ['/admin/deal-management/shared-opportunities/wm/completed']
                        },
                        {
                            label: 'Declined Opportunities',
                            routerLink: ['/admin/deal-management/shared-opportunities/wm/declined']
                        },
                        {
                            label: 'Missed Opportunities',
                            routerLink: ['/admin/deal-management/shared-opportunities/wm/missed']
                        },
                    ]
                },
                {
                    title: 'Shared with Relationship Manager',
                    icon: '/icons/relationship-manager.svg',
                    items: [
                        {
                            label: 'All Opportunities',
                            routerLink: ['/admin/deal-management/shared-opportunities/rm']
                        },
                        {
                            label: 'Live Opportunities',
                            routerLink: ['/admin/deal-management/shared-opportunities/rm/live']
                        },
                        {
                            label: 'Closing Opportunities',
                            routerLink: ['/admin/deal-management/shared-opportunities/rm/closing']
                        },
                        {
                            label: 'Completed Opportunities',
                            routerLink: ['/admin/deal-management/shared-opportunities/rm/completed']
                        },
                        {
                            label: 'Missed Opportunities',
                            routerLink: ['/admin/deal-management/shared-opportunities/rm/missed']
                        },
                    ]
                },
                {
                    title: 'Shared with Clients',
                    icon: '/icons/clients.svg',
                    items: [
                        {
                            label: 'All Opportunities',
                            routerLink: ['/admin/deal-management/shared-opportunities/client']
                        },
                        {
                            label: 'Live Opportunities',
                            routerLink: ['/admin/deal-management/shared-opportunities/client/live']
                        },
                        {
                            label: 'Expressed Interest',
                            routerLink: ['/admin/deal-management/shared-opportunities/client/expressed-interest']
                        },
                        {
                            label: 'Closing Opportunities',
                            items: buildAdminClientClosingMenuItems('/admin/deal-management/shared-opportunities/client'),
                        },
                        {
                            label: 'Completed Opportunities',
                            routerLink: ['/admin/deal-management/shared-opportunities/client/completed']
                        },
                        {
                            label: 'Declined Opportunities',
                            routerLink: ['/admin/deal-management/shared-opportunities/client/declined']
                        },
                        {
                            label: 'Missed Opportunities',
                            routerLink: ['/admin/deal-management/shared-opportunities/client/missed']
                        },
                        {
                            label: 'Withdrawn Opportunities',
                            routerLink: ['/admin/deal-management/shared-opportunities/client/withdrawn']
                        },
                    ]
                }
            ]
        } as ExtendedMenuItem,
        
        ...(!isAdminDealPreparerRole ? [{
            label: 'Users',
            iconPath: '/icons/users.svg',
            items: [
                ...(!isAdminUsersRole ? [{
                    label: 'Admin',
                    routerLink: ['/admin/users/admin-users']
                }] : []),
                {
                    label: 'Wealth Manager Admin',
                    routerLink: ['/admin/users/wealth-managers']
                },
                {
                    label: 'Relationship Managers',
                    routerLink: ['/admin/users/relationship-managers']
                },
                {
                    label: 'Clients',
                    routerLink: ['/admin/users/clients']
                }
            ]
        } as ExtendedMenuItem] : []),
        
        ...(!isAdminDealPreparerRole ? [{
            label: 'Registrations',
            iconPath: '/icons/registration.svg',
            menuType: 'box-submenu',
            expanded: false,
            boxSections: [
                {
                    title: 'BettaAlpha Customer',
                    items: [
                        { label: 'All', routerLink: ['/admin/registrations/bettaalpha-customer/all'] },
                        { label: 'Pending', routerLink: ['/admin/registrations/bettaalpha-customer/pending'] },
                        { label: 'KYC', routerLink: ['/admin/registrations/bettaalpha-customer/kyc'] },
                        { label: 'Due Diligence', routerLink: ['/admin/registrations/bettaalpha-customer/due-diligence'] },
                        { label: 'Partnership Agreement', routerLink: ['/admin/registrations/bettaalpha-customer/partnership-agreement'] },
                        { label: 'Approved', routerLink: ['/admin/registrations/bettaalpha-customer/approved'] },
                        { label: 'Declined', routerLink: ['/admin/registrations/bettaalpha-customer/declined'] },
                        { label: 'Removed', routerLink: ['/admin/registrations/bettaalpha-customer/removed'] },
                        { label: 'Prospect', routerLink: ['/admin/registrations/bettaalpha-customer/prospect'] },
                        { label: 'Not Currently Serviceable', routerLink: ['/admin/registrations/bettaalpha-customer/not-currently-serviceable'] },
                    ]
                },
                {
                    title: 'Product Owners',
                    items: [
                        { label: 'All', routerLink: ['/admin/registrations/product-owner/all'] },
                        { label: 'Pending', routerLink: ['/admin/registrations/product-owner/pending'] },
                        { label: 'KYC', routerLink: ['/admin/registrations/product-owner/kyc'] },
                        { label: 'Due Diligence', routerLink: ['/admin/registrations/product-owner/due-diligence'] },
                        { label: 'Commercial Agreement', routerLink: ['/admin/registrations/product-owner/commercial-agreement'] },
                        { label: 'Approved', routerLink: ['/admin/registrations/product-owner/approved'] },
                        { label: 'Declined', routerLink: ['/admin/registrations/product-owner/declined'] },
                        { label: 'Removed', routerLink: ['/admin/registrations/product-owner/removed'] }
                    ]
                }
            ]
        } as ExtendedMenuItem] : []),
        
        ...(!isAdminUsersRole ? [{
            label: 'Tasks',
            iconPath: '/icons/book.svg',
            menuType: 'box-submenu',
            expanded: false,
            defaultCollapsed: true,
            boxSections: [
                {
                    title: 'Website',
                    sectionKey: 'website',
                    items: [
                        { label: 'Demo Requests', routerLink: ['/admin/new-tasks/website/demo-requests'] },
                        { label: "Let's Talk Requests", routerLink: ['/admin/new-tasks/website/contact-requests'] },
                        { label: 'History', routerLink: ['/admin/new-tasks/website/history'] },
                    ],
                },
                {
                    title: 'Deals',
                    sectionKey: 'deals',
                    items: [
                        { label: 'Deal Approval', routerLink: ['/admin/new-tasks/deals/deal-approval'] },
                        { label: 'Deal Subscription', routerLink: ['/admin/new-tasks/deals/deal-subscription'] },
                        { label: 'Deal Other', routerLink: ['/admin/new-tasks/deals/deal-other'] },
                        { label: 'History', routerLink: ['/admin/new-tasks/deals/history'] },
                    ],
                },
                {
                    title: 'Registrations',
                    sectionKey: 'registrations',
                    items: [
                        { label: 'PO Onboarding', routerLink: ['/admin/new-tasks/registrations/po-onboarding'] },
                        { label: 'WM Onboarding', routerLink: ['/admin/new-tasks/registrations/wm-onboarding'] },
                        { label: 'History', routerLink: ['/admin/new-tasks/registrations/history'] },
                    ],
                },
                // Users tasks hidden for now across portals.
                // {
                //     title: 'Users',
                //     sectionKey: 'users',
                //     items: [
                //         { label: 'Users', routerLink: ['/admin/new-tasks/users/users'] },
                //         { label: 'History', routerLink: ['/admin/new-tasks/users/history'] },
                //     ],
                // },
            ],
        } as ExtendedMenuItem] : []),

        {
            label: 'Notifications',
            iconPath: '/icons/bell-outlined.svg',
            routerLink: ['/notifications']
        } as ExtendedMenuItem,
        {
            label: 'Questions',
            iconPath: '/icons/question-answer-file.svg',
            routerLink: ['/questions']
        } as ExtendedMenuItem,
        {
            label: 'Feedback',
            iconPath: '/icons/message.svg',
            routerLink: ['/feedback']
        } as ExtendedMenuItem,

        ...(!isAdminUsersRole ? [{
            label: 'Specific Deal Requests',
            iconPath: '/icons/deals.svg',
            routerLink: ['/admin/specific-deal-requests']
        } as ExtendedMenuItem] : []),

        {
            label: 'Other',
            iconPath: '/icons/lines.svg',
            menuType: 'submenu',
            items: [
                { label: 'Subscriptions', routerLink: ['/admin/subscriptions'] },
                { label: 'Demo Requests', routerLink: ['/admin/demo-requests'] },
                { label: "Let's Talk Requests", routerLink: ['/admin/lets-talk-requests'] },
            ],
        } as ExtendedMenuItem,

        {
            label: 'Learning Hub',
            iconPath: '/icons/book.svg',
            menuType: 'submenu',
            items: [
                { label: 'Content', routerLink: ['/admin/learning-hub'] },
                ...(isSuperAdmin
                    ? [{ label: 'Manage Sections', routerLink: ['/admin/learning-hub/manage-sections'] }]
                    : []),
            ],
        } as ExtendedMenuItem,
        {
            label: 'My Profile',
            iconPath: '/icons/user.svg',
            routerLink: ['/user-profile']
        } as ExtendedMenuItem,
        
        {
            label: 'Logout',
            iconPath: '/icons/log-out.svg',
            command: logoutCallback
        } as ExtendedMenuItem
    ];
}
